import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { CirclePlus } from "lucide-react";

import NoDataDisplay from "../../../../components/UI/NoDataDisplay";
import MobileActionHeader from "../../../../components/UI/headingsUI/MobileActionHeader";
import { USER_SEARCH_OPTIONS } from "../../utils/userTable.config";
import UsePermissionsClaim from "../../../../CustomHooks/UsePermissionsClaim";
import ButtonIcon from "../../../../components/UI/buttonUI/ButtonIcon";
import sortDataByAlphabetically from "../../../../functions/sorting/sortDataByAlphabetically";
import UserDialogManager from "../../dialogs/userDialogView/UserDialogManager";
import UserBulkActions from "./UserBulkActions";
import UsersList from "./UsersList";
import UserTableHead from "./UserTableHead";
import SearchTable from "../../../../components/common/SearchTable";
import BulkUserDisableDialog from "./BulkUserDisableDialog";
import BulkPhoneVerificationDialog from "./BulkPhoneVerificationDialog";
import ExportButton from "../../../../components/exportExcel/ExportButton";
import { formatUsersExcelData } from "../../utils/excelFormatters"

const UserContainer = () => {
  const usersData = useSelector((state) => state.data.data);
  const originalData = useSelector((state) => state.data.copyData);
  const [userViewDetails, setUserViewDetails] = useState({});
  const [bulkActionType, setBulkActionType] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]); // store selected user IDs
  const [showBulkActionDialog, setShowBulkActionDialog] = useState(false);
  const isSorted = useSelector((state) => state.data.isSorted);
  const dataList = useMemo(() => {
    return isSorted
      ? usersData
      : sortDataByAlphabetically(usersData, ["fullName"]);
  }, [isSorted, usersData]);

  const { checkPermission } = UsePermissionsClaim();
  const permAddUser = checkPermission("add_user");
  const disableUserPerm = checkPermission("disable_user");
  const phoneVerificationPerm = checkPermission("user_phone_verify");

  const filteredDataList = useMemo(() => {
    if (!bulkActionType) return dataList;

    switch (bulkActionType) {
      case "Verify":
        return dataList.filter((user) => user.isPhoneVerified === false);

      case "Unverify":
        return dataList.filter((user) => user.isPhoneVerified === true);

      default:
        return dataList;
    }
  }, [bulkActionType, dataList]);

  const isDataAvailable = filteredDataList.length > 0;

  // Handle single user select toggle
  const handleUserSelect = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id)
        ? prev.filter((userId) => userId !== id)
        : [...prev, id],
    );
  };

  // Select or deselect all
  const handleSelectAllUsers = () => {
    if (selectedUsers.length === filteredDataList.length) {
      setSelectedUsers([]); // unselect all
    } else {
      setSelectedUsers(filteredDataList.map((user) => user.id)); // select all
    }
  };

  // Check if all selected
  const allSelected =
    selectedUsers.length > 0 &&
    selectedUsers.length === filteredDataList.length;

  // ...............get  excel data............
  const excelData = formatUsersExcelData(originalData);
  const excelConfig = {
    data: excelData,
    fileName: "JBCC_Users_List",
    sheetName: "JBCC Users",
  };

  return (
    <>
      <MobileActionHeader
        title="All users"
        navigate={"/user/add"}
        btnText={permAddUser ? "Add User" : undefined}
      />
      <div className="flex justify-between items-center my-3">
        <SearchTable searchCategories={USER_SEARCH_OPTIONS} />
        <div className="flex items-center gap-1 lg:gap-2">
          {(disableUserPerm || phoneVerificationPerm) && (
            <UserBulkActions
              bulkActionType={bulkActionType}
              setBulkActionType={setBulkActionType}
              selectedCount={selectedUsers.length}
              setShowBulkActionDialog={setShowBulkActionDialog}
              setSelectedUsers={setSelectedUsers}
            />
          )}
          <div className="2xs:max-md:hidden">
            {permAddUser && (
              <ButtonIcon
                btnText="Add User"
                Icon={CirclePlus}
                size={19}
                nav="/user/add"
              />
            )}
          </div>
          <ExportButton excelConfig={excelConfig} />
        </div>
      </div>
      <div className="table-container table-height">
        <table className="table relative">
          <UserTableHead
            isSelectionMode={bulkActionType}
            allSelected={allSelected}
            isDataAvailable={isDataAvailable}
            handleSelectAllUsers={handleSelectAllUsers}
          />
          <tbody>
            {isDataAvailable ? (
              <UsersList
                dataList={filteredDataList}
                selectedUsers={selectedUsers}
                isSelectionMode={bulkActionType}
                handleUserSelect={handleUserSelect}
                setUserViewDetails={setUserViewDetails}
              />
            ) : (
              <NoDataDisplay />
            )}
          </tbody>
        </table>
      </div>
      {/* =================== USER DIALOG DETAILS ================= */}
      {Object.entries(userViewDetails).length !== 0 && (
        <UserDialogManager
          closeDialog={() => setUserViewDetails({})}
          userData={userViewDetails}
        />
      )}
      {/* Show confirmation dialog for bulk user disable action */}
      {showBulkActionDialog && bulkActionType === "Disable" && (
        <BulkUserDisableDialog
          closeDialog={() => setShowBulkActionDialog(false)}
          userIDs={selectedUsers}
        />
      )}
      {/* Show confirmation dialog for bulk phone Verify / Unverify actions */}
      {showBulkActionDialog &&
        (bulkActionType === "Verify" || bulkActionType === "Unverify") && (
          <BulkPhoneVerificationDialog
            actionType={bulkActionType}
            closeDialog={() => setShowBulkActionDialog(false)}
            userIDs={selectedUsers}
          />
        )}
    </>
  );
};
export default UserContainer;
